// src/components/Common/BusinessHoursV2.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import BusinessHoursSchedule from './BusinessHoursSchedule';
import HolidayCalendar from './HolidayCalendar';
import { validateActionTarget, ACTIONS_REQUIRING_TARGET } from '../../utils/pbxActionValidation';

interface BusinessHoursV2Props {
  entityType: 'extension' | 'phone_number';
  entityId: string;
  customerID: number;
}

interface DaySchedule {
  day: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  breaks: BreakPeriod[];
}

interface BreakPeriod {
  id?: string;
  type: 'lunch' | 'supper' | 'break' | 'custom';
  name: string;
  startTime: string;
  endTime: string;
  action: number; // pbxapp
  target?: string; // pbxdigits
  description?: string; // pbxdesc
}

interface BusinessHoursData {
  id?: number;
  custid: number;
  extension: string;
  entity_type?: 'extension' | 'phone_number';
  enabled: boolean;
  schedule: DaySchedule[];
  outside_hours_pbxapp: number;
  outside_hours_pbxdigits?: string;
  outside_hours_pbxdesc?: string;
}

interface OverflowAction {
  action_key: number;
  action_name: string;
  requires_target: boolean;
}

interface OverflowTarget {
  value: string;
  name: string;
}

const DAYS_OF_WEEK = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

const BusinessHoursV2: React.FC<BusinessHoursV2Props> = ({ entityType, entityId, customerID }) => {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [businessHours, setBusinessHours] = useState<BusinessHoursData | null>(null);
  const [schedule, setSchedule] = useState<DaySchedule[]>([]);
  const [outsideHoursAction, setOutsideHoursAction] = useState(7); // Default to voicemail
  const [outsideHoursTarget, setOutsideHoursTarget] = useState<string>('');
  
  // Options
  const [overflowActions, setOverflowActions] = useState<OverflowAction[]>([]);
  const [overflowTargets, setOverflowTargets] = useState<OverflowTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  const fetchOverflowTargets = useCallback(async (appId: number) => {
    // Always fetch targets for actions that typically require them
    if (!ACTIONS_REQUIRING_TARGET.includes(appId)) {
      setOverflowTargets([]);
      return;
    }

    setLoadingTargets(true);
    try {
      // Pass customer ID to ensure correct filtering
      const params: Record<string, string> = customerID ? { customerId: String(customerID) } : {};
      const response = await api.get(`/endpoints/ring_groups_v2/overflow-targets/${appId}`, { params });
      if (response.data.success) {
        setOverflowTargets(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch overflow targets:', err);
    } finally {
      setLoadingTargets(false);
    }
  }, [customerID]);

  const fetchBusinessHours = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    try {
      const params: Record<string, string> = customerID ? { customerId: String(customerID) } : {};
      const response = await api.get(`/endpoints/business-hours-v2/${entityType}/${entityId}`, { params });
      if (response.data.success) {
        const data = response.data.data;
        if (data) {
          setBusinessHours(data);
          setEnabled(data.enabled);
          setSchedule(data.schedule || initializeDefaultSchedule());
          setOutsideHoursAction(data.outside_hours_pbxapp || 7);
          setOutsideHoursTarget(data.outside_hours_pbxdigits || '');
          
          if (data.outside_hours_pbxapp) {
            fetchOverflowTargets(data.outside_hours_pbxapp);
          }
        } else {
          // No data exists yet - initialize defaults
          setBusinessHours(null);
          setEnabled(false);
          setSchedule(initializeDefaultSchedule());
          setOutsideHoursAction(7);
          setOutsideHoursTarget('');
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch business hours:', err);
      setError(err.response?.data?.message || 'Failed to load business hours');
      // Initialize default schedule on error
      setSchedule(initializeDefaultSchedule());
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, customerID, fetchOverflowTargets]);

  useEffect(() => {
    fetchBusinessHours();
    fetchOverflowActions();
  }, [entityId, fetchBusinessHours]);

  const initializeDefaultSchedule = (): DaySchedule[] => {
    return DAYS_OF_WEEK.map(day => ({
      day,
      isOpen: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day),
      openTime: '09:00',
      closeTime: '17:00',
      breaks: [],
    }));
  };

  const fetchOverflowActions = async () => {
    try {
      const response = await api.get('/endpoints/ring_groups_v2/overflow-actions');
      if (response.data.success) {
        setOverflowActions(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch overflow actions:', err);
    }
  };

  const handleToggleEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    setEnabled(newEnabled);

    try {
      await saveBusinessHours(newEnabled);
      setSuccess(newEnabled ? 'Business hours enabled' : 'Business hours disabled');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update business hours');
      setEnabled(!newEnabled); // Revert on error
    }
  };

  const saveBusinessHours = async (enabledState?: boolean) => {
    const data: any = {
      custid: customerID,
      extension: entityId,
      entity_type: entityType,
      enabled: enabledState !== undefined ? enabledState : enabled,
      schedule: schedule,
      outside_hours_pbxapp: outsideHoursAction,
      outside_hours_pbxdigits: outsideHoursTarget,
      outside_hours_pbxdesc: undefined, // Will be set by API based on target
    };

    console.log('Saving business hours:', data);
    console.log('Current businessHours state:', businessHours);

    try {
      const params: Record<string, string> = customerID ? { customerId: String(customerID) } : {};
      
      if (businessHours?.id) {
        console.log('Updating existing business hours with ID:', businessHours.id);
        await api.put(`/endpoints/business-hours-v2/${businessHours.id}?${new URLSearchParams(params).toString()}`, data);
      } else {
        console.log('Creating new business hours');
        const response = await api.post('/endpoints/business-hours-v2?' + new URLSearchParams(params).toString(), data);
        console.log('Create response:', response.data);
        if (response.data.success && response.data.data?.id) {
          setBusinessHours({ ...data, id: response.data.data.id });
        } else if (response.data.success) {
          // Refetch to get the ID
          await fetchBusinessHours();
        }
      }
    } catch (error) {
      console.error('Save business hours error:', error);
      throw error;
    }
  };

  const handleScheduleChange = (newSchedule: DaySchedule[]) => {
    setSchedule(newSchedule);
  };

  const handleOutsideHoursChange = (action: number, target?: string) => {
    setOutsideHoursAction(action);
    setOutsideHoursTarget(target || '');
  };

  const handleSave = async () => {
    console.log('Save button clicked');
    setError(null);
    setSuccess(null);

    // Validate outside hours action/target pair
    const validation = validateActionTarget(outsideHoursAction, outsideHoursTarget);
    if (!validation.isValid) {
      setError(validation.error || 'Please select a target for the "Outside Business Hours" action');
      return;
    }

    try {
      await saveBusinessHours();
      setSuccess('Business hours saved successfully');
      // Refresh data after save
      setTimeout(() => {
        fetchBusinessHours();
      }, 500);
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || 'Failed to save business hours');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">
              Business Hours
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={handleToggleEnabled}
                  disabled={loading}
                />
              }
              label={enabled ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !enabled ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Business hours are disabled. Enable them to configure your schedule.
          </Typography>
        ) : (
          <BusinessHoursSchedule
            schedule={schedule}
            outsideHoursAction={outsideHoursAction}
            outsideHoursTarget={outsideHoursTarget}
            onScheduleChange={handleScheduleChange}
            onOutsideHoursChange={handleOutsideHoursChange}
            overflowActions={overflowActions}
            overflowTargets={overflowTargets}
            onFetchTargets={fetchOverflowTargets}
            onSave={handleSave}
          />
        )}
      </Paper>
      
      {/* Holiday Calendar */}
      {enabled && (
        <HolidayCalendar
          entityType={entityType}
          entityId={entityId}
          customerID={customerID}
          year={new Date().getFullYear()}
        />
      )}
    </Box>
  );
};

export default BusinessHoursV2;